import { components } from "@certn/api/generated-schemas/client-portal";
import { BasicButton, Modal, showToast, Text } from "@certn/cascadia-react";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { Bundle } from "../../components/bundle/Bundle";
import { BundleConflictModal } from "../order/steps/components/BundleConflictModal";
import { BundleDetails } from "../order/steps/components/BundleDetails";
import {
  findBundleConflicts,
  findStandaloneChecksAbsorbedByBundle,
  getBundleCheckTypesFromBundle,
  getBundleConflictSummary,
} from "./bundle-conflict-utils";
import { BundleAbsorbChecksModal } from "./BundleAbsorbChecksModal";
import {
  PackageContext,
  SelectedBundlesType,
} from "./package-creation/PackageContext";
import { PackageEditAddBundleModal } from "./PackageEditAddBundleModal";
import { BundleData } from "./types";

function getCheckArgumentRecord(value: unknown): Record<string, unknown> {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return {};
}

function mergeBundleCheckArguments({
  bundleCheckArgsByType,
  userCheckArgsByType,
  checkType,
}: {
  bundleCheckArgsByType: Record<string, unknown>;
  userCheckArgsByType: Record<string, Record<string, unknown>>;
  checkType: string;
}): Record<string, unknown> {
  return {
    ...getCheckArgumentRecord(bundleCheckArgsByType[checkType]),
    ...getCheckArgumentRecord(userCheckArgsByType[checkType]),
  };
}

export function PackageEditBundlesSection() {
  const { t } = useTranslation();
  const { packageData, setPackage } = useContext(PackageContext);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBundleConflictModalOpen, setIsBundleConflictModalOpen] =
    useState(false);
  const [selectedBundle, setSelectedBundle] = useState<BundleData>();
  const [pendingBundle, setPendingBundle] = useState<SelectedBundlesType>();
  const [bundleConflictSummary, setBundleConflictSummary] = useState<{
    conflictingBundleTitles: string[];
    sharedCheckNames: string[];
  }>({ conflictingBundleTitles: [], sharedCheckNames: [] });
  const [isAbsorbChecksModalOpen, setIsAbsorbChecksModalOpen] = useState(false);
  const [absorbedCheckNames, setAbsorbedCheckNames] = useState<string[]>([]);
  const [pendingBundlesToKeep, setPendingBundlesToKeep] = useState<
    SelectedBundlesType[] | undefined
  >();
  const [checkArgs, setCheckArgs] = useState<
    Record<string, Record<string, unknown>>
  >({});

  const prepareBundleForAdd = (bundle: SelectedBundlesType) => {
    const bundleCheckArgs = (bundle.check_types_with_arguments || {}) as Record<
      string,
      unknown
    >;

    return {
      ...bundle,
      checks: bundle?.checks?.map((check) => ({
        ...check,
        check_arguments: mergeBundleCheckArguments({
          bundleCheckArgsByType: bundleCheckArgs,
          userCheckArgsByType: checkArgs,
          checkType: check.type,
        }),
      })),
    };
  };

  const clearPendingAddState = () => {
    setPendingBundle(undefined);
    setPendingBundlesToKeep(undefined);
    setAbsorbedCheckNames([]);
    setIsAbsorbChecksModalOpen(false);
    setIsBundleConflictModalOpen(false);
    setSelectedBundle(undefined);
    setCheckArgs({});
    setIsAddModalOpen(false);
  };

  const commitAddBundle = (
    bundle: SelectedBundlesType,
    bundlesToKeep?: SelectedBundlesType[]
  ) => {
    const bundleCheckTypes = new Set(getBundleCheckTypesFromBundle(bundle));
    const remainingBundles = bundlesToKeep ?? packageData.bundles;

    setPackage({
      ...packageData,
      bundles: [...remainingBundles, bundle],
      checks: packageData.checks.filter(
        (check) => !bundleCheckTypes.has(check.key)
      ),
    });
    showToast(t("{{bundleName}} bundle added", { bundleName: bundle.title }), {
      type: "success",
    });
    clearPendingAddState();
  };

  const requestAddBundle = (
    bundle: SelectedBundlesType,
    bundlesToKeep?: SelectedBundlesType[]
  ) => {
    const preparedBundle = prepareBundleForAdd(bundle);
    const absorbedChecks = findStandaloneChecksAbsorbedByBundle(
      preparedBundle,
      packageData.checks
    );

    if (absorbedChecks.length > 0) {
      setPendingBundle(preparedBundle);
      setPendingBundlesToKeep(bundlesToKeep);
      setAbsorbedCheckNames(absorbedChecks.map((check) => check.name));
      setIsAbsorbChecksModalOpen(true);
      return;
    }

    commitAddBundle(preparedBundle, bundlesToKeep);
  };

  const onAdd = (bundle?: SelectedBundlesType) => {
    if (!bundle) {
      showToast(t("Something went wrong, try again"), { type: "error" });
      return;
    }

    const conflicts = findBundleConflicts(bundle, packageData.bundles);
    if (conflicts.length > 0) {
      setPendingBundle(bundle);
      setBundleConflictSummary(getBundleConflictSummary(conflicts));
      setIsBundleConflictModalOpen(true);
      return;
    }

    requestAddBundle(bundle);
  };

  const onConfirmBundleConflict = () => {
    if (!pendingBundle) {
      showToast(t("Something went wrong, try again"), { type: "error" });
      return;
    }

    const incomingCheckTypes = new Set(
      getBundleCheckTypesFromBundle(pendingBundle)
    );
    const remainingBundles = packageData.bundles.filter((existingBundle) => {
      const bundleCheckTypes = getBundleCheckTypesFromBundle(existingBundle);
      return !bundleCheckTypes.some((checkType) =>
        incomingCheckTypes.has(checkType)
      );
    });

    setIsBundleConflictModalOpen(false);
    requestAddBundle(pendingBundle, remainingBundles);
  };

  const onConfirmAbsorbChecks = () => {
    if (!pendingBundle) {
      showToast(t("Something went wrong, try again"), { type: "error" });
      return;
    }

    commitAddBundle(pendingBundle, pendingBundlesToKeep);
  };

  const onRemove = (bundle?: SelectedBundlesType) => {
    if (!bundle) {
      showToast(t("Something went wrong, try again"), { type: "error" });
      return;
    }

    setPackage({
      ...packageData,
      bundles: packageData.bundles.filter(({ id }) => id !== bundle.id),
    });
    showToast(
      t("{{bundleName}} bundle removed", { bundleName: bundle.title }),
      {
        type: "success",
      }
    );
    setSelectedBundle(undefined);
    setCheckArgs({});
  };

  const handleUpdateArguments = (
    check_arguments: Record<string, unknown>,
    check: components["schemas"]["BundledDealCheckListChild"]
  ) => {
    setCheckArgs((prev) => ({ ...prev, [check.type]: check_arguments }));
  };

  const handleSelectBundleToAdd = (bundle: BundleData) => {
    setCheckArgs({});
    setSelectedBundle(bundle);
    setIsAddModalOpen(false);
  };

  return (
    <div className={styles.container}>
      <Text style="global/heading-medium">{t("Bundled deals")}</Text>
      {packageData.bundles.length === 0 && (
        <Text style="form/body-secondary">
          {t("No bundled deals on this package yet.")}
        </Text>
      )}
      <div className={styles.bundles}>
        {packageData.bundles.map((bundle) => (
          <Bundle
            key={bundle.id}
            bundleName={bundle.title}
            checks={bundle.products.map((product) => product.name)}
            price={bundle.total != null ? bundle.total.toString() : ""}
            onPress={() => {
              setCheckArgs({});
              setSelectedBundle(bundle as unknown as BundleData);
            }}
            onRemove={() => onRemove(bundle)}
            showRemoveWhenSelected
            isSelected
            testID={slugify(bundle.title)}
            currency={bundle.currency}
          />
        ))}
      </div>
      <BasicButton
        inverse
        size="small"
        leftIcon="add"
        onPress={() => setIsAddModalOpen(true)}
        testID="package-edit-add-bundle-button"
      >
        {t("Add bundled deal")}
      </BasicButton>
      {isAddModalOpen && (
        <PackageEditAddBundleModal
          selectedBundleIds={packageData.bundles.map((bundle) => bundle.id)}
          onSelect={handleSelectBundleToAdd}
          onClose={() => setIsAddModalOpen(false)}
        />
      )}
      <Modal
        isOpen={Boolean(selectedBundle)}
        className={styles.modalContainer}
        testID="bundle-details-modal"
      >
        <BundleDetails
          bundle={selectedBundle}
          isBundleApplied={
            !!packageData.bundles?.find(({ id }) => id === selectedBundle?.id)
          }
          onClose={() => {
            setCheckArgs({});
            setSelectedBundle(undefined);
          }}
          onAdd={onAdd}
          onRemove={onRemove}
          handleUpdateArguments={handleUpdateArguments}
        />
        <BundleConflictModal
          isOpen={isBundleConflictModalOpen}
          isLoading={false}
          conflictingBundleTitles={
            bundleConflictSummary.conflictingBundleTitles
          }
          sharedCheckNames={bundleConflictSummary.sharedCheckNames}
          onCancel={() => {
            setIsBundleConflictModalOpen(false);
            setPendingBundle(undefined);
          }}
          onConfirm={onConfirmBundleConflict}
        />
        <BundleAbsorbChecksModal
          isOpen={isAbsorbChecksModalOpen}
          bundleTitle={pendingBundle?.title ?? ""}
          checkNames={absorbedCheckNames}
          onCancel={() => {
            setIsAbsorbChecksModalOpen(false);
            setPendingBundle(undefined);
            setPendingBundlesToKeep(undefined);
            setAbsorbedCheckNames([]);
          }}
          onConfirm={onConfirmAbsorbChecks}
        />
      </Modal>
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
    alignItems: "flex-start",
  }),
  bundles: css({
    display: "flex",
    gap: "var(--space-4)",
    flexWrap: "wrap",
    width: "100%",
  }),
  modalContainer: css({
    width: "100%",
  }),
};
